/**
 * SnapOverridePopup — the Shift+RMB one-shot osnap override grid (Faz 11.2,
 * OCS overlay.rs 4-column 16px icon grid parity).
 *
 * Every running-snap type plus "Mid Between 2 Points (M2P)" at the end.
 * Picking a type arms the classic single-pick override: the NEXT snap query
 * resolves only that type (useSnapStore.overrideType, consumed on the next
 * click). M2P starts a two-click calculator whose midpoint is injected into
 * the active command through the dynamic-input pipeline (injectPointAtWorld
 * — the input injection path stays untouched).
 *
 * Rendered OUTSIDE the R3F canvas (DOM overlay), pointer-transparent except
 * for its own buttons.
 */

import { useEffect } from 'react';
import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';
import { useSnapOverrideStore } from '../../state/useSnapOverrideStore';
import { useSnapStore, SNAP_ICONS, type SnapType } from '../../tools/snapping/SnapManager';
import { worldAtClient, injectPointAtWorld } from '../../core/dynamicInput/dynamicInputEngine';

/** Grid order: the OCS override grid lists running snaps + M2P last. */
const OVERRIDE_TYPES: SnapType[] = [
    'endpoint', 'midpoint', 'center', 'node', 'quadrant',
    'intersection', 'apparentIntersection', 'extension', 'insertion',
    'perpendicular', 'tangent', 'geometricCenter', 'nearest', 'parallel',
];

export function SnapOverridePopup() {
    const { t } = useTranslation();
    const visible = useSnapOverrideStore((s) => s.visible);
    const position = useSnapOverrideStore((s) => s.position);
    const m2pStage = useSnapOverrideStore((s) => s.m2pStage);

    // Outside click closes the grid; Escape also cancels a running M2P.
    useEffect(() => {
        if (!visible) return;
        const onDown = (e: PointerEvent) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest?.('[data-snap-override]')) return;
            useSnapOverrideStore.getState().close();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                useSnapOverrideStore.getState().close();
                useSnapOverrideStore.getState().cancelM2p();
            }
        };
        const timer = window.setTimeout(() => {
            document.addEventListener('pointerdown', onDown, true);
            document.addEventListener('keydown', onKey);
        }, 0);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('pointerdown', onDown, true);
            document.removeEventListener('keydown', onKey);
        };
    }, [visible]);

    // M2P two-click calculator: window capture-phase clicks are intercepted
    // (tools never see them), the midpoint rides injectPointAtWorld back into
    // the active command as a normal synthesized click.
    useEffect(() => {
        if (m2pStage === 0) return;
        const onDown = (e: PointerEvent) => {
            if (e.pointerId === 991) return; // synthetic dynamic click
            if (e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();
            const world = worldAtClient(e.clientX, e.clientY);
            if (!world) return;
            const store = useSnapOverrideStore.getState();
            const p: [number, number, number] = [world.x, world.y, world.z];
            if (store.m2pStage === 1) {
                store.setM2pFirst(p);
            } else if (store.m2pStage === 2 && store.m2pFirst) {
                const a = store.m2pFirst;
                const point = new THREE.Vector3(
                    (a[0] + p[0]) / 2,
                    (a[1] + p[1]) / 2,
                    (a[2] + p[2]) / 2
                );
                store.finishM2p();
                injectPointAtWorld(point);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') useSnapOverrideStore.getState().cancelM2p();
        };
        window.addEventListener('pointerdown', onDown, true);
        window.addEventListener('keydown', onKey, true);
        return () => {
            window.removeEventListener('pointerdown', onDown, true);
            window.removeEventListener('keydown', onKey, true);
        };
    }, [m2pStage]);

    // One-shot override consumption: the click AFTER the query used the
    // override, so the first pointerup clears it (classic single-pick life).
    useEffect(() => {
        const onUp = (e: PointerEvent) => {
            if (e.pointerId === 991) return;
            useSnapStore.getState().consumeOverride();
        };
        window.addEventListener('pointerup', onUp, true);
        return () => window.removeEventListener('pointerup', onUp, true);
    }, []);

    const pick = (type: SnapType) => {
        useSnapStore.getState().setOverrideType(type);
        useSnapOverrideStore.getState().close();
    };

    const clampedX = Math.min(position.x, (typeof window !== 'undefined' ? window.innerWidth : 800) - 260);
    const clampedY = Math.min(position.y, (typeof window !== 'undefined' ? window.innerHeight : 600) - 180);

    return (
        <>
            {visible && (
                <Box
                    data-snap-override
                    position="fixed"
                    left={Math.max(8, clampedX)}
                    top={Math.max(8, clampedY)}
                    bg="bg.panel"
                    border="1px solid"
                    borderColor="border.base"
                    borderRadius="sm"
                    boxShadow="md"
                    p={2}
                    zIndex={30}
                >
                    <Box px={1} pb={1} fontSize="10px" color="text.secondary" textTransform="uppercase">
                        {t('snapOverride.title')}
                    </Box>
                    {/* 4-column 16px icon grid (OCS overlay.rs layout): the
                        width hugs the widest row, labels never clip. */}
                    <Grid templateColumns="repeat(4, 1fr)" gap={1} w="max">
                        {OVERRIDE_TYPES.map((type) => (
                            <Flex
                                key={type}
                                as="button"
                                direction="column"
                                align="center"
                                justify="center"
                                minW="52px"
                                px={1}
                                py={1}
                                borderRadius="sm"
                                color="text.primary"
                                fontSize="15px"
                                lineHeight="1.2"
                                cursor="pointer"
                                _hover={{ bg: 'bg.active' }}
                                onClick={() => pick(type)}
                                title={t(`statusbar.snapType.${type}`)}
                            >
                                <Box as="span">{SNAP_ICONS[type]}</Box>
                                <Text fontSize="8px" color="text.secondary" noOfLines={1} maxW="52px">
                                    {t(`statusbar.snapType.${type}`).replace(/ snap$/i, '')}
                                </Text>
                            </Flex>
                        ))}
                        {/* M2P — always the last cell (OCS grid contract). */}
                        <Flex
                            as="button"
                            direction="column"
                            align="center"
                            justify="center"
                            minW="52px"
                            px={1}
                            py={1}
                            borderRadius="sm"
                            color="text.primary"
                            cursor="pointer"
                            _hover={{ bg: 'bg.active' }}
                            onClick={() => useSnapOverrideStore.getState().beginM2p()}
                            title={t('snapOverride.m2p')}
                            border="1px dashed"
                            borderColor="border.base"
                        >
                            <Box as="span" fontSize="13px" fontWeight="bold">M2P</Box>
                            <Text fontSize="8px" color="text.secondary" noOfLines={1}>
                                {t('snapOverride.m2pShort')}
                            </Text>
                        </Flex>
                    </Grid>
                </Box>
            )}
            {/* M2P follow-up prompt: a small non-blocking cue while the two
                clicks are collected. */}
            {m2pStage > 0 && (
                <Box
                    position="fixed"
                    left="50%"
                    bottom="56px"
                    transform="translateX(-50%)"
                    bg="bg.panel"
                    border="1px solid"
                    borderColor="border.base"
                    borderRadius="sm"
                    boxShadow="md"
                    px={3}
                    py={1}
                    fontSize="xs"
                    color="text.primary"
                    pointerEvents="none"
                    zIndex={40}
                >
                    {m2pStage === 1 ? t('snapOverride.m2pFirst') : t('snapOverride.m2pSecond')}
                </Box>
            )}
        </>
    );
}

export default SnapOverridePopup;
